import React from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../../design/avatar";
import { Heading, Subheading } from "../../design/heading";
import { Select } from "../../design/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import VisitStatusBadge from "../../components/VisitStatusBadge";
import CreateVisitButton from "../../components/CreateVisitButton";
import moment from "moment";
import { Stat } from "../../components/Stat";
import { getInitials } from "../../utils/string";
import {
  DashboardHeaderProps,
  DashboardStatsProps,
  VisitTableRowProps,
  VisitTableProps,
  DashboardContentProps,
  DashboardPageProps,
  TimeFrame,
} from "./types";
import { useDashboardDetail, DashboardDemographics } from "./hooks/useDashboardDetail";

// Extracted components for better organization
const DashboardHeader: React.FC<DashboardHeaderProps> = React.memo(({ timeFrame, onTimeFrameChange }) => (
  <div className="flex flex-col gap-4 mt-8 sm:flex-row sm:items-end sm:justify-between">
    <Subheading>Overview</Subheading>
    <div className="w-full sm:w-auto">
      <Select
        name="period"
        value={timeFrame}
        onChange={(e) => onTimeFrameChange(e.target.value as TimeFrame)}
        className="w-full sm:w-48"
      >
        <option value="last_week">Last week</option>
        <option value="last_month">Last month</option>
        <option value="last_quarter">Last quarter</option>
      </Select>
    </div>
  </div>
));

const DashboardStats: React.FC<DashboardStatsProps> = React.memo(({ dashboardData }) => {
  return (
    <div className="grid grid-cols-1 gap-6 mt-8 sm:grid-cols-3">
      <Stat
        title="Completed Visits"
        value={`${dashboardData?.number_of_visits ?? 0}`}
      />
      <Stat
        title="Upcoming Visits"
        value={`${dashboardData?.number_of_upcoming_visits ?? 0}`}
      />
      <Stat
        title="Total Events"
        value={`${dashboardData?.number_of_events ?? 0}`}
      />
    </div>
  );
});

const VisitTableRow: React.FC<VisitTableRowProps> = React.memo(({ visit, caseInfo }) => {
  const formatDuration = React.useCallback((startDate: any, endDate: any) => {
    if (!startDate || !endDate) return "N/A";
    
    const durationMs = endDate.toDate().getTime() - startDate.toDate().getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    return `${minutes}m`;
  }, []);
  
  const formattedDate = React.useMemo(() => {
    return visit.startDate ? moment(visit.startDate.toDate()).format("MMM D, YYYY h:mm A") : "N/A";
  }, [visit.startDate]);
  
  const supervisorName = React.useMemo(() => {
    return visit.supervisor ? `${visit.supervisor.firstName} ${visit.supervisor.lastName}` : null;
  }, [visit.supervisor]);
  
  return (
    <TableRow key={visit.id} href={`/visit/${visit.id}`} title={`View visit details for ${caseInfo?.identifier || 'Unknown Case'}`}>
      <TableCell>
        {visit.supervisor && (
          <div className="flex items-center gap-2">
            <Avatar
              src={visit.supervisor.avatarUrl}
              className="size-6"
              initials={getInitials(supervisorName || "")}
            />
            <span className="truncate" title={supervisorName || ""}>{supervisorName}</span>
          </div>
        )}
      </TableCell>
      <TableCell title={formattedDate}>
        {formattedDate}
      </TableCell>
      <TableCell title={formatDuration(visit.startDate, visit.endDate)}>
        {formatDuration(visit.startDate, visit.endDate)}
      </TableCell>
      <TableCell className="font-mono text-sm" title={caseInfo?.identifier || "Unknown Case"}>
        {caseInfo?.identifier || "N/A"}
      </TableCell>
      <TableCell>
        <VisitStatusBadge status={visit.status} />
      </TableCell>
    </TableRow>
  );
});

const VisitTable: React.FC<VisitTableProps> = React.memo(({ visits, cases }) => (
  <>
    <Subheading className="mt-14">Upcoming Visits</Subheading>
    {visits.length === 0 && (
      <div className="px-4 py-12 mt-8 text-center border border-dashed rounded-lg border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-zinc-100 dark:bg-zinc-800">
          <svg className="w-8 h-8 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="mb-2 text-lg font-medium text-zinc-900 dark:text-zinc-100">No upcoming visits</h3>
        <p className="mb-6 text-zinc-500 dark:text-zinc-400">Schedule your first visit to get started with monitoring.</p>
        <CreateVisitButton />
      </div>
    )}
    {visits.length > 0 && (
      <div className="mt-8 overflow-hidden">
        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]" striped>
          <TableHead>
            <TableRow>
              <TableHeader>Monitor</TableHeader>
              <TableHeader>Date</TableHeader>
              <TableHeader>Duration</TableHeader>
              <TableHeader>Case</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {visits.map((visit) => {
              const caseInfo = cases.find((c) => c.id === visit.caseId);
              return (
                <VisitTableRow key={visit.id} visit={visit} caseInfo={caseInfo} />
              );
            })}
          </TableBody>
        </Table>
      </div>
    )}
  </>
));

interface DemographicsSummaryProps {
  demographics: DashboardDemographics;
}

const DemographicsSummary: React.FC<DemographicsSummaryProps> = React.memo(({ demographics }) => {
  const { totalChildren, topRace, topAgeGroup, sexDistribution } = demographics;
  const totalWithSex = sexDistribution.male + sexDistribution.female;

  if (totalChildren === 0) {
    return null;
  }

  return (
    <>
      <div className="flex items-center justify-between mt-14">
        <Subheading>Demographics Overview</Subheading>
        <Link
          to="/demographics"
          className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
        >
          View Full Report →
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-4 mt-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="p-4 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
          <div className="text-sm text-zinc-500 dark:text-zinc-400">Total Children</div>
          <div className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-white">{totalChildren}</div>
        </div>
        {topAgeGroup && (
          <div className="p-4 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
            <div className="text-sm text-zinc-500 dark:text-zinc-400">Most Common Age</div>
            <div className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-white">{topAgeGroup.value}</div>
            <div className="text-xs text-zinc-400 dark:text-zinc-500">{topAgeGroup.percentage}% of children</div>
          </div>
        )}
        {topRace && (
          <div className="p-4 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
            <div className="text-sm text-zinc-500 dark:text-zinc-400">Most Common Race</div>
            <div className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-white truncate" title={topRace.value}>{topRace.value}</div>
            <div className="text-xs text-zinc-400 dark:text-zinc-500">{topRace.percentage}% of children</div>
          </div>
        )}
        {totalWithSex > 0 && (
          <div className="p-4 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
            <div className="text-sm text-zinc-500 dark:text-zinc-400">Sex Distribution</div>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1">
                <div className="h-2 bg-zinc-100 dark:bg-zinc-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${Math.round((sexDistribution.male / totalWithSex) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
            <div className="mt-1 flex justify-between text-xs text-zinc-400 dark:text-zinc-500">
              <span>Male: {sexDistribution.male}</span>
              <span>Female: {sexDistribution.female}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
});

const DashboardContent: React.FC<DashboardContentProps> = React.memo(({
  visits,
  cases,
  dashboardData,
  demographicsSummary,
}) => (
  <>
    <DashboardStats dashboardData={dashboardData} />
    <VisitTable visits={visits} cases={cases} />
    <DemographicsSummary demographics={demographicsSummary} />
  </>
));

const DashboardPage: React.FC<DashboardPageProps> = React.memo(({
  visits,
  cases,
  dashboardData,
  demographicsSummary,
  timeFrame,
  onTimeFrameChange
}) => (
  <>
    <Heading>Dashboard</Heading>
    <DashboardHeader timeFrame={timeFrame} onTimeFrameChange={onTimeFrameChange} />
    <DashboardContent
      visits={visits}
      cases={cases}
      dashboardData={dashboardData}
      demographicsSummary={demographicsSummary}
    />
  </>
));

// Skeleton loading components
const StatSkeleton: React.FC = () => (
  <div>
    <div className="h-px bg-zinc-200 dark:bg-zinc-700" />
    <div className="h-4 mt-6 rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
    <div className="h-8 mt-3 rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
  </div>
);

const TableSkeleton: React.FC = () => (
  <div className="mt-8">
    <div className="w-32 h-6 mb-4 rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex space-x-4">
          <div className="flex-1 h-4 rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
          <div className="flex-1 h-4 rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
          <div className="flex-1 h-4 rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
          <div className="flex-1 h-4 rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
          <div className="w-20 h-4 rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
        </div>
      ))}
    </div>
  </div>
);

// Error state component
const ErrorState: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="px-4 py-12 text-center">
    <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full dark:bg-red-900/20">
      <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    </div>
    <h3 className="mb-2 text-lg font-medium text-zinc-900 dark:text-zinc-100">Failed to load dashboard</h3>
    <p className="mb-6 text-zinc-500 dark:text-zinc-400">There was an error loading your dashboard data. Please try again.</p>
    <button 
      onClick={onRetry}
      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
    >
      Try Again
    </button>
  </div>
);

const DashboardPageContainer: React.FC = () => {
  const {
    visits,
    cases,
    dashboardData,
    demographicsSummary,
    timeFrame,
    loading,
    error,
    handleTimeFrameChange,
    reloadDashboards,
  } = useDashboardDetail();

  const handleRetry = React.useCallback(() => {
    reloadDashboards(timeFrame);
  }, [reloadDashboards, timeFrame]);

  if (error) {
    return (
      <div className="space-y-8">
        <Heading>Dashboard</Heading>
        <ErrorState onRetry={handleRetry} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <Heading>Dashboard</Heading>
        <div className="flex flex-col gap-4 mt-8 sm:flex-row sm:items-end sm:justify-between">
          <Subheading>Overview</Subheading>
          <div className="w-32 h-10 rounded bg-zinc-200 dark:bg-zinc-700 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 gap-6 mt-8 sm:grid-cols-3">
          <StatSkeleton />
          <StatSkeleton />
          <StatSkeleton />
        </div>
        <TableSkeleton />
      </div>
    );
  }

  return (
    <DashboardPage
      visits={visits}
      cases={cases}
      dashboardData={dashboardData}
      demographicsSummary={demographicsSummary}
      timeFrame={timeFrame}
      onTimeFrameChange={handleTimeFrameChange}
    />
  );
};

export default DashboardPageContainer;
